/**
 * Back, one level at a time: the decisions, with no DOM and no Telegram in
 * them (lib/nav.ts wires them to both; scripts/verify/nav-back.ts tests them).
 *
 * A layer is anything over the main tabs: a sheet (`.sheet`) or a screen
 * pushed in place of a tab (`[data-layer]`: the client card, a Settings
 * page). Layers come in document order, so the last one is on top: sheets
 * are portals at the end of <body>, after the screens.
 */

export interface LayerInfo {
  kind: 'sheet' | 'screen';
  /** It has its own back control (`[data-back]`): «Закрыть», «‹ Клиенты». */
  hasBack: boolean;
}

/** What Telegram's back arrow is for: nothing on the main tabs. */
export const showBack = (layers: LayerInfo[]) => layers.length > 0;

/**
 * What one press of back does, on the top layer only: its own back
 * control when it has one; for a sheet without one, a tap on its backdrop
 * (which closes every sheet that closes on a tap outside); otherwise
 * nothing — better than leaving the app.
 */
export function backAction(layers: LayerInfo[]): { layer: number; via: 'back' | 'backdrop' } | null {
  if (layers.length === 0) return null;
  const top = layers.length - 1;
  if (layers[top].hasBack) return { layer: top, via: 'back' };
  if (layers[top].kind === 'sheet') return { layer: top, via: 'backdrop' };
  return null;
}

/**
 * Unsaved input, by layer: typed into and not saved yet. Telegram asks
 * before closing only while one of these is still open.
 */
export type Unsaved = ReadonlySet<string>;

export type UnsavedEvent =
  /** Typing in a field of this layer. Fields on the main tabs (search) are not tracked. */
  | { type: 'input'; layer: string | null; field: 'text' | 'search' | 'other'; skip?: boolean }
  /** «Сохранить» (or any `[data-save]`) pressed in this layer. */
  | { type: 'save'; layer: string | null }
  /** The layers still open: one that closed takes its unsaved input with it. */
  | { type: 'open'; layers: string[] };

export function nextUnsaved(state: Unsaved, e: UnsavedEvent): Unsaved {
  switch (e.type) {
    case 'input':
      if (!e.layer || e.skip || e.field === 'search' || state.has(e.layer)) return state;
      return new Set([...state, e.layer]);
    case 'save':
      if (!e.layer || !state.has(e.layer)) return state;
      return new Set([...state].filter((x) => x !== e.layer));
    case 'open': {
      const open = new Set(e.layers);
      const kept = [...state].filter((x) => open.has(x));
      return kept.length === state.size ? state : new Set(kept);
    }
  }
}

export const askBeforeClosing = (state: Unsaved) => state.size > 0;
